import React from 'react';
import { roundToNearestHalf } from '@express-labs/raven-tools';
import Link from '../Link/Link';
import s from './StarRating.scss';

export type Rating = 0 | 0.5 | 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 4.5 | 5;
export type StarRatingProps = {
  className?: string;
  /** @deprecated `bv` mode is deprecated. Leave this prop undefined to fall back to the default `svg` mode */
  mode?: 'svg' | 'bv';
  rating?: Rating;
  size?: 'small' | 'large' | 'larger';
  totalReviewCount?: number;
  productReviewsURL?: string;
};

const DEFAULT_RATING = 0;
const DEFAULT_MODE = 'svg';
const DEFAULT_SIZE = 'large';

const RenderGifStars = ({
  rating,
  size,
}: Required<Pick<StarRatingProps, 'size' | 'rating'>>) => {
  let containerClassName = null;
  switch (size) {
    case 'small':
      containerClassName = s.starRatingImgSmall;
      break;
    case 'larger':
      containerClassName = s.starRatingImgLarger;
      break;
    default:
      // 'large' is the default prop
      containerClassName = s.starRatingImgLarge;
  }

  return (
    <img
      alt={`${rating} stars`}
      className={`bvStars ${containerClassName}`}
      src={`//express.ugc.bazaarvoice.com/6549/${rating}/5/rating.gif`}
    />
  );
};

type StarProps = {
  key: number;
  empty?: boolean;
};

const Star = ({ empty = false }: StarProps) => {
  if (empty) {
    return (
      <svg
        width="13"
        height="13"
        viewBox="0 0 13 13"
        xmlns="http://www.w3.org/2000/svg"
        data-testid="empty-star"
      >
        <path
          d="m6.5 0 1.855 4.579L13 4.99 9.477 8.184 10.55 13 6.5 10.442 2.45 13l1.073-4.816L0 4.991l4.645-.412L6.5 0Zm0 2.662-.928 2.292-.23.567-.609.054-2.369.21 1.83 1.658.432.39-.127.569-.551 2.468 2.018-1.273.534-.337.534.337L9.05 10.87l-.55-2.468-.127-.568.431-.391 1.83-1.658-2.368-.21-.61-.054-.229-.567L6.5 2.662Z"
          fill="#000"
          fillRule="nonzero"
        />
      </svg>
    );
  }

  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="filled-star"
    >
      <path
        d="m13 4.991-4.645-.412L6.5 0 4.645 4.579 0 4.991l3.523 3.193L2.449 13 6.5 10.442 10.551 13 9.477 8.184z"
        fill="#000"
        fillRule="nonzero"
      />
    </svg>
  );
};

const HalfStar = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="half-star"
  >
    <path
      d="m6.5 0 1.855 4.579L13 4.99 9.477 8.184 10.55 13 6.5 10.442 2.45 13l1.073-4.816L0 4.991l4.645-.412L6.5 0Zm0 2.662-.004.01v6.589l.538.336L9.05 10.87l-.55-2.468-.127-.568.431-.391 1.83-1.658-2.368-.21-.61-.054-.229-.567L6.5 2.662Z"
      fill="#000"
      fillRule="nonzero"
    />
  </svg>
);

type RenderSvgStarsProps = Required<Pick<StarRatingProps, 'size' | 'rating'>> &
  Pick<StarRatingProps, 'className' | 'totalReviewCount' | 'productReviewsURL'>;

const RenderSvgStars = ({
  className,
  rating,
  size,
  totalReviewCount,
  productReviewsURL,
}: RenderSvgStarsProps) => {
  const fullStars = Math.floor(rating);
  const halfStars = rating - Math.floor(rating);
  const blankStars = 5 - Math.ceil(rating);
  const uniqueId = `${Date.now() + Math.random()}`;

  let containerClassName = null;
  switch (size) {
    case 'small':
      containerClassName = s.svgContainerSmall;
      break;
    case 'larger':
      containerClassName = s.svgContainerLarger;
      break;
    default:
      // 'large' is the default prop
      containerClassName = s.svgContainerLarge;
  }

  const displayCount = totalReviewCount ? totalReviewCount > 0 : false;
  return (
    <div className={s.starRatingContainer}>
      <span className={['StarRating', containerClassName, className].join(' ')}>
        <span className="srOnly" id={uniqueId}>
          {rating} out of 5 stars
        </span>
        <span role="img" aria-labelledby={uniqueId}>
          {[...Array(fullStars)].map((undef, i) => (
            <Star key={i} />
          ))}
          {halfStars > 0 && <HalfStar />}
          {[...Array(blankStars)].map((undef, i) => (
            <Star key={i} empty />
          ))}
        </span>
      </span>
      {displayCount && (
        <Link theme="dark" size="body" href={productReviewsURL}>
          <div className={s.ratingText}>
            <span className={s.reviewCount}>{rating}</span>
            <span className={s.reviewCount}>({totalReviewCount})</span>
          </div>
        </Link>
      )}
    </div>
  );
};

/**
 * StarRating displays 5 stars that are filled-in by half-step increments
 * starting at 0 and ending at 5 full stars.
 *
 * Please note that we no longer are using Bazaarvoice.  The `bv` option for mode is deprecated.
 *
 * ## Global Class Names
 * This is a map of the global css classes for this component.  You can hook into these classes to further customize the StarRating.
 *
 * ```html
 * <span class='StarRating [props.className]'>
 *   <span class='srOnly' />
 *   <span> ... stars go here </span>
 * </span>

 * ```
 */
const StarRating = ({
  className,
  mode = DEFAULT_MODE,
  rating = DEFAULT_RATING,
  size = DEFAULT_SIZE,
  totalReviewCount = 0,
  productReviewsURL,
}: StarRatingProps) => {
  // BV Mode is deprecated
  const productRating = roundToNearestHalf(rating) as Rating;
  if (mode === 'bv') {
    return <RenderGifStars size={size} rating={productRating} />;
  }
  return (
    <RenderSvgStars
      size={size}
      rating={productRating}
      className={className}
      totalReviewCount={totalReviewCount}
      productReviewsURL={productReviewsURL}
    />
  );
};

StarRating.displayName = 'StarRating';
export default StarRating;
